import { useState } from "react";
import { Bell } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Loader } from "@/components/shared/loader";
import { NotificationDetailSheet } from "@/features/notifications/components/NotificationDetailSheet";
import { NotificationList } from "@/features/notifications/components/NotificationList";
import {
  useMarkAllNotificationsRead,
  useNotificationInbox,
  useUnreadNotificationCount,
} from "@/features/notifications/hooks/useNotifications";
import { Pages } from "@/constants/enums";
import { appPath } from "@/constants/paths";
import { cn } from "@/lib/utils";
import type { UserNotification } from "@/types/notification";

const NOTIFICATIONS_URL = appPath(Pages.NOTIFICATIONS);
const DROPDOWN_PREVIEW_SIZE = 8;

function formatBadgeCount(count: number): string {
  return count > 9 ? "9+" : String(count);
}

interface NotificationBellDropdownProps {
  align?: "start" | "center" | "end";
}

export function NotificationBellDropdown({
  align = "end",
}: NotificationBellDropdownProps) {
  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState<UserNotification | null>(null);

  const { data: unreadCount = 0, refetch: refetchUnread } = useUnreadNotificationCount();
  const { data, isLoading, refetch: refetchInbox } = useNotificationInbox({
    page: 1,
    pageSize: DROPDOWN_PREVIEW_SIZE,
  });
  const markAllRead = useMarkAllNotificationsRead();

  const items = data?.items ?? [];

  async function handleOpenChange(next: boolean) {
    setOpen(next);
    if (next) {
      await Promise.all([refetchUnread(), refetchInbox()]);
    }
  }

  function handleSelect(notification: UserNotification) {
    setSelected(notification);
    setDetailOpen(true);
    setOpen(false);
  }

  async function handleMarkAllRead() {
    await markAllRead.mutateAsync();
  }

  return (
    <>
      <DropdownMenu open={open} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative size-9 shrink-0 rounded-full"
            aria-label="الإشعارات"
          >
            <Bell className="size-5" aria-hidden />
            {unreadCount > 0 && (
              <span
                className="absolute -top-0.5 -left-0.5 flex min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-4 text-destructive-foreground"
                aria-live="polite"
              >
                {formatBadgeCount(unreadCount)}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align={align}
          side="bottom"
          className={cn(
            "w-80 overflow-hidden rounded-xl border border-border bg-popover p-0 text-popover-foreground shadow-lg",
          )}
        >
          <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
            <h2 className="text-sm font-semibold">الإشعارات</h2>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto px-2 py-1 text-xs"
                onClick={() => void handleMarkAllRead()}
                disabled={markAllRead.isPending}
              >
                تعليم الكل كمقروء
              </Button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader />
              </div>
            ) : (
              <NotificationList
                items={items}
                onSelect={handleSelect}
                compact
              />
            )}
          </div>

          <div className="border-t border-border p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-center text-xs"
              asChild
              onClick={() => setOpen(false)}
            >
              <Link to={NOTIFICATIONS_URL}>عرض الكل</Link>
            </Button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <NotificationDetailSheet
        notificationId={selected?.id ?? null}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        preview={selected ?? undefined}
      />
    </>
  );
}
